import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Button as MuiButton, Grid, Stack } from '@mui/material';

import {
  MasterIndexApi,
  MasterIndexApiPath,
  MasterVehicleApi,
  VehicleApi,
} from '@/api';
import {
  FormCard,
  FormRoot,
  MasterNameAutocomplete,
  NoResultItem,
  RenderFormField,
  PageHeader,
} from '@/components';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { withLoader, WithLoaderProps } from '@/hocs';
import withUnsavedChangesWarning, {
  WithUnsavedChangesWarningProps,
} from '@/hocs/withUnsavedChangesWarning';
import { useFilesValidation, useFormConfigLoader } from '@/hooks';
import { useMasterVehicleFormOptions } from '@/hooks/useMasterVehicleFormOptions';
import { TabAnimationWrapper, useTabLayoutContext } from '@/layouts/TabLayout';
import {
  IMasterName,
  IMasterNamePerson,
  IMasterVehicleForm,
  IMasterVehicleLicensePlate,
  IOffenseOptionItem,
} from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import MasterIndexVehicleNotes from '@/pages/MasterIndex/Vehicles/MasterIndexVehiclesNotes';
import { uploadFiles } from '@/services';
import { ConfirmTypes, useDataStore, useToastStore } from '@/store';

import { useMasterVehicleContext } from '..';
import {
  AutomobileTypeFormFields,
  MasterVehicleFormFields,
  MasterVehicleNextPaths,
  MasterVehicleOwnerTableColumns,
  MasterVehicleScreens,
  VehicleTypeFormFields,
} from '../data';
import {
  buildMasterVehicleTitle,
  prepareLicensePlatesForSubmission,
} from '../licensePlate.utils';
import { MasterVehicleLicensePlatesField } from './MasterVehicleLicensePlatesField';
import { MasterVehicleOwnerTable } from './tables/MasterVehicleOwnerTable';

export interface MasterVehicleFormData
  extends Omit<IMasterVehicleForm, 'licensePlate' | 'licensePlates' | 'owner'> {
  licensePlate?: IMasterVehicleLicensePlate;
  licensePlates?: IMasterVehicleLicensePlate[];
  owner?: IMasterName;
  uniqueId?: number;
}

export interface MasterVehicleFormProps {
  isMasterIndex?: boolean;
  hideHeader?: boolean;
  hideButtons?: boolean;
  formId?: string;
  goBack?: () => void;
  onSuccess?: () => void;
}

const MasterVehicleForm = ({
  showLoader,
  hideLoader,
  setIsDirty,
  onNavigation,
  isMasterIndex,
  hideHeader,
  formId,
  goBack,
}: WithLoaderProps &
  WithUnsavedChangesWarningProps &
  MasterVehicleFormProps) => {
  const { updateToast } = useToastStore();
  const refreshMakes = useDataStore((state) => state.refreshMakes);
  const { currentVehicleId, handleAddLinkedVehicle } =
    useMasterVehicleContext();
  const { updateCurrentScreen } = useTabLayoutContext();
  const { filteredOptions: VehicleTypeOptions } = useFormConfigLoader({
    optionsName: 'PROPERTY',
    category: 'PROPERTY',
  });

  const { id: vehicleId } = useParams();

  const VehicleTypeList = getOffenseOptionItems(
    'VEHICLE_PROPERTY_TYPES',
    VehicleTypeOptions,
  ) as Map<string, IOffenseOptionItem>;
  const { handleSubmit, watch, setValue, trigger } =
    useFormContext<MasterVehicleFormData>();
  const owner = watch('owner');
  const [ownerInput, setOwnerInput] = useState<IMasterName | undefined>(
    undefined,
  );
  const makeName = watch('make');
  const modelName = watch('model');
  const year = watch('year');
  const vehicleType = watch('type');
  const colorName = watch('color');

  const uploadedFileItems = watch('files');
  const hasInvalidFiles = useFilesValidation(uploadedFileItems);

  const [customMakes, setCustomMakes] = useState<Map<string, string>>(
    new Map(),
  );
  const [customModels, setCustomModels] = useState<Map<string, string>>(
    new Map(),
  );
  const [localMakeOptions, setLocalMakeOptions] = useState<any[]>([]);
  const [localModelOptions, setLocalModelOptions] = useState<any[]>([]);
  const [localColorOptions, setLocalColorOptions] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('information');

  const {
    makeOptions,
    modelOptions,
    yearOptions,
    colorOptions,
    licensePlateStateOptions,
    licensePlateTypeOptions,
  } = useMasterVehicleFormOptions(vehicleType, makeName, modelName, year);

  // Merge API options with local custom options
  const allMakeOptions = useMemo(() => {
    const combined = [...makeOptions, ...localMakeOptions];

    // Ensure current make value is included in options
    if (makeName && !combined.find((option) => option.value === makeName)) {
      combined.push({
        label: makeName,
        value: makeName,
        _id: `current-${makeName}`,
      });
    }

    // Remove duplicates based on name
    return combined.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.label === item.label),
    );
  }, [makeOptions, localMakeOptions, makeName]);

  const allModelOptions = useMemo(() => {
    const combined = [...modelOptions, ...localModelOptions];

    if (modelName && !combined.find((option) => option.value === modelName)) {
      const matchingOptionById = combined.find(
        (option) => option._id === modelName,
      );

      if (matchingOptionById) {
        combined.push({
          label: matchingOptionById.label,
          value: modelName,
          _id: `current-${modelName}`,
        });
      } else {
        combined.push({
          label: modelName,
          value: modelName,
          _id: `current-${modelName}`,
        });
      }
    }

    // Remove duplicates based on name
    return combined.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.label === item.label),
    );
  }, [modelOptions, localModelOptions, modelName]);

  const allColorOptions = useMemo(() => {
    const combined = [...colorOptions, ...localColorOptions];

    // Ensure current color value is included in options
    if (colorName && !combined.find((option) => option.value === colorName)) {
      combined.push({
        label: colorName,
        value: colorName,
      });
    }

    // Remove duplicates based on name
    return combined.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.label === item.label),
    );
  }, [colorOptions, localColorOptions, colorName]);

  const getItems = (itemName: string) => {
    switch (itemName) {
      case 'make':
        return allMakeOptions;
      case 'model':
        return allModelOptions;
      case 'year':
        return yearOptions;
      case 'color':
        return allColorOptions;
      case 'type': {
        return Array.from(VehicleTypeList.values()).map((vehicleTypeItem) => ({
          value: vehicleTypeItem.key,
          label: vehicleTypeItem.label,
        }));
      }
      default:
        return undefined;
    }
  };

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (
        VehicleTypeList?.size &&
        _value.type === VehicleTypeList?.get('AUTOMOBILES')?.key
      ) {
        if (name === 'model' && type === 'change') {
          setValue('color', '');
        }
        if (name === 'make' && type === 'change') {
          setValue('model', '');
          setValue('color', '');
        }
        if (name === 'year' && type === 'change') {
          setValue('model', '');
          setValue('color', '');
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, VehicleTypeList, setValue]);

  // Fix for NHTSA IDs being stored instead of model names
  // When model options are loaded, check if the current model value is an NHTSA ID
  // and correct it to the human-readable model name
  useEffect(() => {
    if (modelName && modelOptions.length > 0) {
      // Check if current model value matches an option's _id (NHTSA ID)
      const matchingOption = modelOptions.find(
        (option) => (option as { _id?: string })._id === modelName,
      );
      if (matchingOption && matchingOption.value !== modelName) {
        // The current value is an NHTSA ID, correct it to the model name
        setValue('model', String(matchingOption.value ?? ''), {
          shouldDirty: false,
        });
      }
    }
  }, [modelName, modelOptions, setValue]);

  const onGoBack = () => {
    setCustomMakes(new Map());
    setCustomModels(new Map());
    setLocalColorOptions([]);

    if (goBack) {
      goBack();
    } else {
      updateCurrentScreen(MasterVehicleScreens.MasterVehicleList);
    }
  };

  const onSubmit = async ({
    files,
    ...restFormValues
  }: MasterVehicleFormData) => {
    setIsDirty(false);
    try {
      showLoader();

      const formData = JSON.parse(JSON.stringify(restFormValues));
      const licensePlatePayload = prepareLicensePlatesForSubmission(
        formData.licensePlates,
      );
      formData.licensePlates = licensePlatePayload.licensePlates;
      delete formData.licensePlate;

      // Ensure make/model relationship exists first
      if (
        formData.vehicleType === VehicleTypeList.get('AUTOMOBILES')?.key &&
        formData.make &&
        formData.model
      ) {
        await ensureModelMakeRelationship(
          formData.make,
          formData.model,
          formData.year,
        );
      }

      const payload = {
        ...formData,
        owner: formData.owner?._id ?? null,
        make: formData.make, // Send name, not ID
        model: formData.model, // Send name, not ID
        files,
        year: formData.year || '',
      };

      if (files && files.length) {
        const uploadedFiles = await uploadFiles(files);
        if (!uploadedFiles) return;
        payload.files = uploadedFiles;
      }

      const api = isMasterIndex ? MasterIndexApi : MasterVehicleApi;

      const submitFunction =
        formData._id && formData._id !== 'undefined'
          ? api.updateMasterVehicle
          : api.createMasterVehicle;

      const res = await submitFunction(payload);

      if (!formData._id && res.data._id) {
        await handleAddLinkedVehicle(res.data._id);
      }
      if (formData._id && formData._id !== 'undefined') {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: `Vehicle updated successfully`,
        });
      } else {
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: `Vehicle created successfully`,
        });
      }
      // if (onSuccess) {
      //   onSuccess();
      // } else {
      // }
      onGoBack();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const ensureModelMakeRelationship = async (
    makeNameParam: string,
    modelNameParam: string,
    yearParam?: number | string,
  ) => {
    try {
      let makeId = getMakeIdFromName(makeNameParam);

      if (!makeId) {
        const makeResponse = await VehicleApi.createVehicleMake({
          name: makeNameParam,
          externalId: Date.now(),
          vehicleType,
        });
        makeId = makeResponse.data._id;
        setCustomMakes((prev) => new Map(prev.set(makeNameParam, makeId)));
        refreshMakes();
      }

      if (makeId && modelNameParam) {
        const existingModel = customModels.get(modelNameParam);
        if (!existingModel) {
          try {
            const modelResponse = await VehicleApi.createVehicleModel({
              name: modelNameParam,
              make: makeId,
              year: yearParam || new Date().getFullYear(),
              vehicleType,
            });
            if (modelResponse.data?._id) {
              const modelId = modelResponse.data._id;
              setCustomModels(
                (prev) => new Map(prev.set(modelNameParam, modelId as string)),
              );
            }
          } catch (modelErr: any) {
            // Model might already exist, ignore duplicate errors
            if (
              !modelErr.message?.includes('E11000') &&
              !modelErr.message?.includes('duplicate key')
            ) {
              throw modelErr;
            }
          }
        }
      }
    } catch (err: any) {
      // Only throw if it's not a duplicate error
      if (
        !err.message?.includes('E11000') &&
        !err.message?.includes('duplicate key')
      ) {
        throw err;
      }
    }
  };

  const handleChange = (newValue: IMasterName) => {
    if (!newValue._id) return;
    setIsDirty(true);
    setOwnerInput(undefined);
    setValue('owner', newValue as IMasterNamePerson);
  };

  const handleColorChange = async (name: string, value: string) => {
    if (!value) {
      setValue('color', '');
      return;
    }
    const existingOption = getItems(name)?.find(
      (option) => option.value === value,
    );
    if (makeName && modelName && !existingOption) {
      try {
        const makeId = getMakeIdFromName(makeName);

        const filteredModelOptions = modelOptions?.filter(
          (option) => option.label === modelName,
        ) as { label: string; value: string; _id: string }[];

        let modelId =
          filteredModelOptions.length > 0
            ? filteredModelOptions[0]._id
            : customModels.get(modelName);

        const isNhtsaModelId =
          typeof modelId === 'string' && modelId.startsWith('nhtsa-');
        if (isNhtsaModelId && makeId) {
          const modelResponse = await VehicleApi.createVehicleModel({
            name: modelName,
            make: makeId,
            year: year || new Date().getFullYear(),
            vehicleType,
          });
          if (modelResponse.data?._id) {
            modelId = modelResponse.data._id;
            setCustomModels(
              (prev) => new Map(prev.set(modelName, modelId as string)),
            );
          }
        }

        if (makeId && modelId && !modelId.startsWith('nhtsa-')) {
          await VehicleApi.createVehicleColor({
            name: value,
            make: makeId,
            model: modelId,
            year: year || new Date().getFullYear(),
          });
          setLocalColorOptions((prev) => [...prev, { label: value, value }]);
        }
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    }
    setIsDirty(true);
    setValue('color', value);
  };

  const getMakeIdFromName = (name: string) => {
    const filteredMakeOptions = allMakeOptions.filter(
      (option) => option.label === name,
    );
    if (filteredMakeOptions.length > 0) {
      return filteredMakeOptions[0]._id;
    }

    return customMakes.get(name);
  };

  const handleMakeChange = async (newMake: { name: string; _id?: string }) => {
    if (!newMake.name) return;
    setIsDirty(true);
    const selectedMake = allMakeOptions.find(
      (makeOption) => makeOption.value === newMake.name,
    );
    if (!selectedMake) {
      try {
        const response = await VehicleApi.createVehicleMake({
          name: newMake.name,
          externalId: Date.now(),
          vehicleType,
        });
        newMake._id = response.data._id;
        if (response.data?._id) {
          const makeId = response.data._id;
          setCustomMakes((prev) => new Map(prev.set(newMake.name, makeId)));

          // Add the new make to local options so it appears in the dropdown
          const newMakeOption = {
            label: newMake.name,
            value: newMake.name,
            _id: makeId,
          };
          setLocalMakeOptions((prev) => [...prev, newMakeOption]);

          // Refresh the Zustand store so the new make persists across form instances
          refreshMakes();
        }
      } catch (err: any) {
        if (
          err.message?.includes('E11000') ||
          err.message?.includes('duplicate key')
        ) {
          const existingMakeId = customMakes.get(newMake.name);
          if (existingMakeId) {
            newMake._id = existingMakeId;
          }
        } else {
          updateToast({ open: true, message: err.message });
        }
      }
    }

    setValue('make', newMake.name);
  };

  const handleModelChange = async (newModel: {
    name: string;
    _id?: string;
  }) => {
    if (!newModel.name) return;
    setIsDirty(true);

    // Check if model needs to be created in the database
    // NHTSA IDs (like 'nhtsa-3679') are not real database IDs, so treat them as if no ID exists
    const hasRealId = newModel._id && !newModel._id.startsWith('nhtsa-');

    if (!hasRealId && makeName) {
      try {
        let makeId = getMakeIdFromName(makeName);

        if (!makeId) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          makeId = getMakeIdFromName(makeName);
        }

        if (makeId) {
          const response = await VehicleApi.createVehicleModel({
            name: newModel.name,
            make: makeId,
            year: year || new Date().getFullYear(),
            vehicleType,
          });
          newModel._id = response.data._id;
          const modelId = response.data?._id;
          if (modelId) {
            setCustomModels(
              (prev) => new Map(prev.set(newModel.name, modelId)),
            );

            // Add the new model to local options so it appears in the dropdown
            const newModelOption = {
              label: newModel.name,
              value: newModel.name,
              _id: modelId,
            };
            setLocalModelOptions((prev) => [...prev, newModelOption]);
          }
        }
      } catch (err: any) {
        if (
          err.message?.includes('E11000') ||
          err.message?.includes('duplicate key')
        ) {
          const makeId = getMakeIdFromName(makeName);
          if (makeId && year) {
            try {
              const modelsResponse = await VehicleApi.getVehicleModels(
                makeId,
                Number(year),
                vehicleType,
              );
              const existingModel = modelsResponse.data.find(
                (m) =>
                  m.name.toLowerCase() === newModel.name.toLowerCase() &&
                  m._id &&
                  !m._id.startsWith('nhtsa-'),
              );
              if (existingModel?._id) {
                const modelId = existingModel._id as string;
                newModel._id = modelId;
                setCustomModels(
                  (prev) => new Map(prev.set(newModel.name, modelId)),
                );
              }
            } catch (modelErr: any) {
              updateToast({ open: true, message: modelErr.message });
            }
          }
        } else {
          updateToast({ open: true, message: err.message });
          return; // Don't set the value if there was an error
        }
      }
    }

    setValue('model', newModel.name, { shouldDirty: true, shouldTouch: true });
    trigger('model');
  };

  const getYearAsNumber = (): number | undefined => {
    if (year === undefined || year === null || year === '') {
      return undefined;
    }
    return typeof year === 'number' ? year : parseInt(String(year), 10);
  };

  const isColorDisabled = !makeName || !modelName;

  const handleInputChange = (fieldName: string, value: any) => {
    if (
      fieldName === 'make' &&
      vehicleType === VehicleTypeList.get('AUTOMOBILES')?.key
    ) {
      handleMakeChange(value as { name: string; _id?: string });
    } else if (
      fieldName === 'model' &&
      vehicleType === VehicleTypeList.get('AUTOMOBILES')?.key
    ) {
      handleModelChange(value as { name: string; _id?: string });
    } else if (
      fieldName === 'color' &&
      vehicleType === VehicleTypeList.get('AUTOMOBILES')?.key
    ) {
      handleColorChange(fieldName, value as string);
    } else if (fieldName === 'year') {
      setIsDirty(true);
      setValue('year', value);
    } else {
      // Handle other fields generically
      setIsDirty(true);
      setValue(fieldName as any, value);
    }
  };

  const getTabs = () => {
    return [
      {
        label: 'Vehicle information',
        path: 'information',
      },
      {
        label: 'Related records',
        path: 'related',
        disabled: !currentVehicleId && !vehicleId,
      },
      {
        label: 'Notes',
        path: 'notes',
        disabled: !currentVehicleId && !vehicleId,
      },
    ];
  };

  const tabs = getTabs();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <>
            {VehicleTypeList?.size > 0 && (
              <FormRoot
                id={formId || (isMasterIndex ? 'master-index-form' : undefined)}
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                  pb: 0,
                  '.p-kase-edit &': {
                    padding: '20px',
                    mt: 0,
                  },
                  '::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                }}
              >
                {MasterVehicleFormFields.map(({ key, title, items }) => {
                  const isInformationCard = key === 'information';
                  const isMasterIndexInformationCard =
                    isMasterIndex && isInformationCard;
                  return (
                    <FormCard
                      key={key}
                      title={title}
                      className="grey-title"
                      sx={
                        isMasterIndexInformationCard
                          ? {
                              borderRadius: '12px',
                              '& .cardContent': {
                                padding: {
                                  xs: '18px 16px 20px',
                                  md: '10px 14px 16px',
                                },
                              },
                            }
                          : undefined
                      }
                    >
                      {items.map(({ grid, ...rest }: any) => (
                        <>
                          <Grid
                            key={rest.name}
                            xs={12}
                            sm={6}
                            md={
                              key === 'information' && rest.name === 'vin'
                                ? 12
                                : 3
                            }
                            {...grid}
                            item
                          >
                            {key === 'information' && rest.name === 'vin' ? (
                              <Stack
                                sx={{
                                  display: 'grid',
                                  gap: 1.5,
                                  gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'repeat(4, minmax(0, 1fr)) 34px 136px',
                                  },
                                }}
                              >
                                <RenderFormField
                                  items={getItems(rest.name)}
                                  {...rest}
                                  inputValue={rest.value}
                                />
                              </Stack>
                            ) : (
                              <RenderFormField
                                items={getItems(rest.name)}
                                {...rest}
                                inputValue={rest.value}
                                {...(rest.name === 'type' && {
                                  handleChange: () => {
                                    setValue('year', undefined);
                                    setValue('make', '');
                                    setValue('model', '');
                                    setValue('color', '');
                                    setValue('style', undefined);
                                    setCustomMakes(new Map());
                                    setCustomModels(new Map());
                                    setLocalColorOptions([]);
                                  },
                                })}
                              />
                            )}
                          </Grid>
                          {key === 'information' && rest.name === 'vin' && (
                            <Grid item xs={12}>
                              <MasterVehicleLicensePlatesField
                                licensePlateStateOptions={
                                  licensePlateStateOptions
                                }
                                licensePlateTypeOptions={
                                  licensePlateTypeOptions
                                }
                              />
                            </Grid>
                          )}
                          {rest.name === 'type' &&
                            vehicleType &&
                            key === 'description' && (
                              <>
                                <Grid item xs={9} />
                                {(vehicleType ===
                                VehicleTypeList.get('AUTOMOBILES')?.key
                                  ? AutomobileTypeFormFields
                                  : VehicleTypeFormFields
                                ).map(
                                  ({ grid: innerGrid, ...innerRest }: any) => {
                                    const fieldProps = {
                                      ...innerRest,
                                      items: getItems(innerRest.name),
                                      value: innerRest.name || '',
                                      handleChange: handleInputChange,
                                      ...(innerRest.name === 'make' && {
                                        vehicleType,
                                      }),

                                      ...(innerRest.name === 'model' &&
                                        vehicleType ===
                                          VehicleTypeList.get('AUTOMOBILES')
                                            ?.key && {
                                          makeId: getMakeIdFromName(makeName),
                                          makeName,
                                          year: getYearAsNumber(),
                                          vehicleType,
                                          disabled: !makeName,
                                          sx: { width: '100%' },
                                        }),
                                      ...(innerRest.name === 'color' && {
                                        disabled: isColorDisabled,
                                      }),
                                    };

                                    return (
                                      <Grid
                                        key={innerRest.name}
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        {...innerGrid}
                                        item
                                      >
                                        <RenderFormField {...fieldProps} />
                                      </Grid>
                                    );
                                  },
                                )}
                              </>
                            )}
                        </>
                      ))}
                      {key === 'ownerInformation' && (
                        <div style={{ width: '100%', padding: '10px' }}>
                          <MasterNameAutocomplete
                            value={ownerInput}
                            excludedMasterNames={owner ? [owner] : []}
                            includeOrganizations
                            onChange={handleChange}
                            noResults={<NoResultItem />}
                          />
                          <MasterVehicleOwnerTable
                            columns={MasterVehicleOwnerTableColumns}
                            owners={owner ? [owner] : []}
                            registeredOwnerId={owner?._id}
                            onSelectOwner={() => setValue('owner', undefined)}
                          />
                        </div>
                      )}
                    </FormCard>
                  );
                })}
              </FormRoot>
            )}
          </>
        );

      case 'related':
        return (
          <MasterIndexRelatedRecords
            type={MasterIndexApiPath.Vehicles}
            viewId={currentVehicleId || vehicleId}
          />
        );

      case 'notes':
        return (
          <MasterIndexVehicleNotes viewId={currentVehicleId || vehicleId} />
        );

      default:
        return null;
    }
  };

  const tabsWithHandlers = tabs.map((tab) => ({
    ...tab,
    onClick: () => setActiveTab(tab.path),
  }));

  const getVehicleTitle = () => {
    const formValues = watch();

    if (currentVehicleId && formValues.uniqueId) {
      return (
        buildMasterVehicleTitle({
          uniqueId: formValues.uniqueId,
          licensePlate: formValues.licensePlate,
          licensePlates: formValues.licensePlates,
          make: formValues.make,
          model: formValues.model,
        }) || 'New vehicle'
      );
    } else {
      return 'New vehicle';
    }
  };

  const renderActionButtons = () => {
    if (activeTab !== 'information') {
      return null;
    }

    return (
      <Stack direction="row" spacing={2}>
        <MuiButton color="inherit" variant="outlined" onClick={onGoBack}>
          Cancel
        </MuiButton>
        <MuiButton
          color="error"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={hasInvalidFiles}
        >
          Save
        </MuiButton>
      </Stack>
    );
  };

  return (
    <TabAnimationWrapper
      nextPaths={MasterVehicleNextPaths.MasterAddVehicleDetail}
    >
      <div className="bg-[#F5F7F7] dark:bg-[#0B1922] flex flex-col h-full overflow-hidden text-gray-900 dark:text-gray-100">
        {!hideHeader && (
          <div className="p-2 flex-shrink-0">
            <PageHeader
              title={getVehicleTitle()}
              onBack={() => {
                if (onNavigation(new Event('back'))) {
                  onGoBack();
                }
              }}
              action={!isMasterIndex ? renderActionButtons() : undefined}
            />
          </div>
        )}

        <div className="!w-full px-4">
          {!hideHeader && tabs && (
            <NavigationButtonGroup
              tabs={tabsWithHandlers}
              activeTab={activeTab}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto px-4 mb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {renderTabContent()}
        </div>
      </div>
    </TabAnimationWrapper>
  );
};

export default withLoader(withUnsavedChangesWarning(MasterVehicleForm, false));
